import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Send, CheckCircle2, XCircle, Bot, User as UserIcon, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export type HubKey = "pms" | "documents" | "commercial";

interface Props {
  hub: HubKey;
  title: string;
  subtitle: string;
  context?: string;
  accent?: string; // tailwind text color e.g. text-[#288CC8]
}

interface Msg { role: "user" | "assistant"; content: string }

const HUB_LABELS: Record<HubKey, string> = {
  pms: "Vel-iO PMS Manager",
  documents: "Vel-iO Document Controller",
  commercial: "Vel-iO Commercial Director",
};

export const AIManagerPanel = ({ hub, title, subtitle, context, accent = "text-[#288CC8]" }: Props) => {
  const { user, fullName } = useAuth();
  const qc = useQueryClient();
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: `Hello ${fullName || "team"} — I'm your ${HUB_LABELS[hub]}. Ask me anything about this hub, or click "Generate draft actions" and I'll surface what needs attention.` }
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Action queue
  const { data: queue = [], refetch: refetchQueue } = useQuery({
    queryKey: ["ai_action_queue", hub],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_action_queue")
        .select("*")
        .eq("hub", hub)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
  });

  const pending = queue.filter(q => q.status === "pending");
  const reviewed = queue.filter(q => q.status !== "pending");

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const newMsgs: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setStreaming(true);
    let assistantSoFar = "";

    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-manager`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ hub, mode: "chat", messages: newMsgs, context }),
      });

      if (resp.status === 429) { toast.error("Rate limit. Please wait."); setStreaming(false); return; }
      if (resp.status === 402) { toast.error("AI credits exhausted."); setStreaming(false); return; }
      if (!resp.ok || !resp.body) { toast.error("AI failed to respond."); setStreaming(false); return; }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;

      // initial assistant placeholder
      setMessages(m => [...m, { role: "assistant", content: "" }]);

      while (!done) {
        const { value, done: rDone } = await reader.read();
        if (rDone) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, idx);
          buf = buf.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") { done = true; break; }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantSoFar += delta;
              setMessages(m => m.map((msg, i) => i === m.length - 1 ? { ...msg, content: assistantSoFar } : msg));
            }
          } catch {
            buf = line + "\n" + buf; break;
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Chat failed");
    } finally {
      setStreaming(false);
    }
  };

  const generateDrafts = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-manager", {
        body: {
          hub,
          mode: "draft_actions",
          messages: [{ role: "user", content: `Review the current ${hub} hub state and propose 3-5 draft actions that need human attention right now. Focus on overdue, critical, or safety/compliance items.` }],
          context,
        },
      });
      if (error) throw error;
      const actions = data?.actions || [];
      if (!actions.length) { toast.info("No actions surfaced — everything looks healthy."); return; }

      const rows = actions.map((a: any) => ({
        hub,
        action_type: a.action_type || "advisory",
        title: a.title,
        summary: a.summary,
        risk_level: a.risk_level || "low",
        status: "pending",
        related_resource_type: a.related_resource_type || null,
        related_resource_id: a.related_resource_id || null,
        created_by_ai: HUB_LABELS[hub],
        ai_model: "google/gemini-2.5-flash",
      }));

      const { error: insErr } = await supabase.from("ai_action_queue").insert(rows);
      if (insErr) throw insErr;
      toast.success(`${actions.length} draft action(s) ready for review`);
      refetchQueue();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to generate drafts");
    } finally {
      setGenerating(false);
    }
  };

  const decide = async (id: string, status: "approved" | "rejected") => {
    const { error } = await supabase
      .from("ai_action_queue")
      .update({
        status,
        reviewed_by: user?.id,
        reviewed_by_name: fullName,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success(status === "approved" ? "Approved — logged for execution" : "Rejected");
    refetchQueue();
  };

  const riskTone = (r: string) =>
    r === "high" ? "bg-[#C83228] text-white" :
    r === "medium" ? "bg-[#E8A020] text-white" :
    "bg-[#288CC8] text-white";

  return (
    <div className="space-y-6">
      <Card className="border-[#D0DDE6]">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className={`flex items-center gap-2 ${accent}`}>
                <Sparkles className="w-5 h-5" /> {title}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              {pending.length > 0 && (
                <Badge className="bg-[#E8A020] text-white">{pending.length} pending</Badge>
              )}
              <Button size="sm" onClick={generateDrafts} disabled={generating} className="bg-[#00283C] hover:bg-[#00283C]/90 text-white">
                {generating ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Generate draft actions
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="chat" className="w-full">
        <TabsList className="bg-[#F4F7FA]">
          <TabsTrigger value="chat">Chat with AI</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Actions {pending.length > 0 && <Badge className="ml-2 bg-[#E8A020] text-white">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history">Audit Log ({reviewed.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <Card className="border-[#D0DDE6]">
            <CardContent className="p-0">
              <ScrollArea className="h-[420px] p-4" ref={scrollRef as any}>
                <div className="space-y-3">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      {m.role === "assistant" && (
                        <div className="w-8 h-8 rounded-full bg-[#00283C] flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-[#288CC8]" />
                        </div>
                      )}
                      <div className={`rounded-lg px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-[#288CC8] text-white" : "bg-[#F4F7FA] text-[#00283C]"}`}>
                        {m.content || (streaming && i === messages.length - 1 ? "..." : "")}
                      </div>
                      {m.role === "user" && (
                        <div className="w-8 h-8 rounded-full bg-[#288CC8]/20 flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-4 h-4 text-[#288CC8]" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="p-3 border-t border-[#D0DDE6] flex gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={`Ask the ${HUB_LABELS[hub]} anything…`}
                  rows={2}
                  className="resize-none"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                />
                <Button onClick={send} disabled={streaming || !input.trim()} className="bg-[#288CC8] hover:bg-[#288CC8]/90 self-end">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card className="border-[#D0DDE6]">
            <CardContent className="p-4 space-y-3">
              {pending.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-[#2CA048]" />
                  No pending actions. Click <strong>Generate draft actions</strong> to have the AI scan this hub.
                </div>
              )}
              {pending.map((a: any) => (
                <div key={a.id} className="border border-[#D0DDE6] rounded-lg p-3 bg-white">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={riskTone(a.risk_level)}>{a.risk_level.toUpperCase()}</Badge>
                        <Badge variant="outline" className="text-xs">{a.action_type}</Badge>
                        {a.risk_level === "high" && <AlertTriangle className="w-4 h-4 text-[#C83228]" />}
                      </div>
                      <div className="font-semibold text-[#00283C]">{a.title}</div>
                      <div className="text-sm text-muted-foreground mt-1">{a.summary}</div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => decide(a.id, "rejected")}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button size="sm" onClick={() => decide(a.id, "approved")} className="bg-[#2CA048] hover:bg-[#2CA048]/90 text-white">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border-[#D0DDE6]">
            <CardContent className="p-4 space-y-2">
              {reviewed.length === 0 && <div className="text-center py-8 text-muted-foreground">No reviewed actions yet.</div>}
              {reviewed.map((a: any) => (
                <div key={a.id} className="border border-[#D0DDE6]/50 rounded p-2 text-sm flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-[#00283C]">{a.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.reviewed_by_name || "Unknown"} • {new Date(a.reviewed_at || a.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge className={a.status === "approved" ? "bg-[#2CA048] text-white" : "bg-[#6B8A9A] text-white"}>
                    {a.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIManagerPanel;
